import { useHistoryWithReferrer } from '@v2/utils/history';

import { IProduct, INutrient } from '@interfaces';

import Clickable from '@v2/components/common/presentational/clickables/default';
import Checkbox from '@components/common/presentational/checkbox/checkbox';

import { mapVeggieBundle } from '@v2/components/pages/shop/utils';
import { useAddAgeBundle } from '@v2/components/common/products/utils';
import { useAgeBundlePDPModal } from '@v2/components/common/presentational/modal/common';
import { useWarningModal } from '@components/common/presentational/modal/common';
import { useBundleUpgradeModal } from '@v2/components/common/presentational/modal/common/bundle.upgrade';
import { apiImageSource } from '@v2/utils/http';
import { useIsSwapping } from '@components/common/products/utils';

import CheckIcon from '@img/shop/check-icon.svg';
import iconWarning from '@img/shop/Warning.svg';
import iconQuestion from '@img/shop/question-bundle.svg';
import { WPImage } from '@v2/utils/image';
import { calculateBundleTotal } from '@v2/utils/cart';
import Tooltip from '@v2/components/common/presentational/tooltip/tooltip';

function BundleActions({ product, disabled, subscribed, setFilter, isVeggie, setIsVeggie, isBuyAgain, isMobile, onBundlePress }
    : { product: IProduct, disabled: boolean, subscribed?: boolean, setFilter?: Function, isVeggie?: boolean, setIsVeggie?: Function, isBuyAgain?: boolean, isMobile?: boolean,
        onBundlePress?: Function }) {

    const onAddBundle = useAddAgeBundle();
    const bundleTotal = calculateBundleTotal(product, true);
    const bundleDiscountTotal = calculateBundleTotal(product);
    const isSwapping = useIsSwapping();

    const box: any = {
        lines: {},
        blueprint: {},
        properties: {
            isVegan: isVeggie,
            productId: product.id
        }
    }

    const [showBundleUpgradeModal] = useBundleUpgradeModal(box, true);

    return (
        <div className={`action-wrapper ${isMobile ? 'mobile' : 'desktop'}`}>
            <div>
                <Clickable onClick={onBundlePress}>
                    <div className="title">
                        {product.title}
                    </div>
                </Clickable>
                {(bundleTotal > 0 && bundleDiscountTotal > 0) &&
                    <div className="info">
                        {subscribed
                            ?
                            <>
                                <span className="regular">${bundleTotal.toFixed(2)}</span>
                                <Tooltip
                                    showOnClick={true}
                                    className="tooltip"
                                    width={250}
                                    description={`Optional Subscribe & Save for an additional 15% off`}>
                                    <span className="discount">${bundleDiscountTotal.toFixed(2)}</span>
                                </Tooltip>  | {product.size} items
                            </>
                            :
                            <>
                                <span className="default">${bundleTotal.toFixed(2)}</span> | {product.size} items
                            </>
                        }
                    </div>
                }
            </div>
            <div className="actions">

                {!isBuyAgain &&
                    <>
                        {setIsVeggie
                            ?
                            <div className={`veggie ${isMobile ? 'mobile' : 'desktop'}`}>
                                <Checkbox
                                    name={'isVeggie'}
                                    size={'MINI'}
                                    type={'SWITCH'}
                                    onChange={() => setIsVeggie(isVeggie ? false : true)}
                                    checked={isVeggie}
                                    useParentState={true}
                                >
                                    Vegan
                                </Checkbox>
                            </div>
                            :
                            <Clickable className="show-all" onClick={() => setFilter(null)}>
                                Show all bundles
                            </Clickable>
                        }
                    </>
                }
                <div className={`action add ${disabled ? 'd-none' : ''}`}>
                    {product?.status !== 'outofstock' &&
                        <button
                            className="button primary blue"
                            onClick={() => onAddBundle(product, isVeggie, (isBuyAgain || isSwapping) ? null : subscribed)}
                            disabled={disabled}
                            aria-label={'Add to bag'}>
                            {isSwapping ? 'Select' : 'Add to bag'}
                        </button>
                    }
                    {isBuyAgain &&
                        <button
                            className="button link blue bundle-picker"
                            onClick={showBundleUpgradeModal}
                            aria-label={'Add to bag'}
                            disabled={disabled}>
                            Choose different age range
                        </button>
                    }
                </div>
            </div>
        </div>
    )
}

export default function Bundle({ product, disabled, subscribed, setFilter, isVeggie, setIsVeggie, fixedHeight, isBuyAgain, staticPDP }
    : {
        product: IProduct, disabled: boolean, subscribed?: boolean, setFilter?: Function, isVeggie?: boolean, setIsVeggie?: Function,
        fixedHeight?: boolean, isBuyAgain?: boolean, staticPDP?: boolean
    }
) {
    const history = useHistoryWithReferrer();

    product = isVeggie ? mapVeggieBundle(product) : product;

    const [showBundlePDP] = useAgeBundlePDPModal(product, true);
    const [showWarningModal] = useWarningModal();
    const image = product.hash ? apiImageSource(`${isVeggie ? 'veggie-' : ''}bundle/${product.id}/${product.hash}/`) : product.mainImage
    const isSwapping = useIsSwapping();

    const handleOpenBundle = () => (disabled || staticPDP || isSwapping) ? showBundlePDP() : history.pushWithReferrer(`/bundle/age/${product.slug}`)

    return (
        <>
            <div className={`product ${product.availability === 'outofstock' ? 'out-of-stock' : ''} highlight-product`}>
                {product.developmental &&
                    <div className="developmental">
                        <div className="brain">
                            <h3>
                                {product.developmental.brainText}
                                <hr />
                            </h3>
                            <span>
                                <WPImage src={product.developmental.brainImage} alt={product.developmental.title} />
                            </span>
                        </div>
                        <div className="details">
                            <h3>{product.developmental.title}</h3>
                            <div className="text" style={fixedHeight ? { paddingRight: 8, maxHeight: 140, overflow: 'auto' } : {}}>
                                {product.developmental.description}
                            </div>
                        </div>
                    </div>
                }
                <div className="product-link horizontal">
                    <div className="details-wrapper">
                        <Clickable className="image-highlight-wrapper" onClick={handleOpenBundle}>
                            <div className="image image-highlight">
                                <img src={image} alt={product.mainImageAlt || product.title} />
                            </div>
                            {isBuyAgain &&
                                <>
                                    <Clickable className="highlight-action" onClick={() => setFilter(null)}>
                                        <img src={iconQuestion} alt="" />
                                    </Clickable>
                                    <Clickable className="warning-action" onClick={showWarningModal}>
                                        <img src={iconWarning} alt="" />
                                    </Clickable>
                                </>
                            }
                        </Clickable>
                        <BundleActions
                            onBundlePress={handleOpenBundle}
                            product={product}
                            disabled={disabled}
                            subscribed={subscribed}
                            setFilter={setFilter}
                            isVeggie={isVeggie}
                            setIsVeggie={setIsVeggie}
                            isBuyAgain={isBuyAgain}
                            isMobile={true} />
                        <Clickable className={`details`} onClick={handleOpenBundle}>
                            <div className="list">
                                <div className="items">
                                    <span>Including</span>
                                    {product.products.map((product: IProduct, index: number) =>
                                        <span key={index}>{`${product.size} x ${product.title}`}</span>
                                    )}
                                </div>
                                <div className="nutrients">
                                    {product.nutrients.map((nutrient: INutrient, key: number) =>
                                        nutrient.source && <span key={key}><img src={CheckIcon} alt="" /> {nutrient.source}</span>
                                    )}
                                </div>
                            </div>
                        </Clickable>
                    </div>
                    <BundleActions
                        onBundlePress={handleOpenBundle}
                        product={product}
                        disabled={disabled}
                        setFilter={setFilter}
                        isVeggie={isVeggie}
                        setIsVeggie={setIsVeggie}
                        isBuyAgain={isBuyAgain}
                        subscribed={subscribed}
                        isMobile={false} />
                </div>
                <div className="out-of-stock-info">out of stock!</div>
                <div className="recommended-info">Recommended</div>
            </div>
        </>
    );
}